import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFetchGet } from '../../utils/fetch/useFetchGet';
import { pathsApi } from '../../router/paths';
import i18n from '../../utils/i18n';
import Body from '../../ui/Body/body';
import GridRow from '../../ui/GridRow';
import { IPriceAttr, IPriceAttrGroup } from './types';
import GridCol from '../../ui/GridCol';
import Details from '../../ui/Details/details';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { ButtonVariationEnum } from '../../ui/Button/types';
import Popup from '../../ui/Popup';
import Input from '../../ui/Input';
import { PopupSize } from '../../ui/Popup/Popup';
import { FetchApi } from '../../utils/fetch/fetchApi';
import DragAndDropPopup from '../../components/DragAndDrop/dragAndDropPopup';
import Dropdown from '../../ui/Dropdown';

const PriceAttr = () => {
    const { data, setData } = useFetchGet<Array<IPriceAttrGroup>>({ path: pathsApi.priceAttrAll });
    const [isNewOpen, handleNewOpen] = useState<false | number>(false);
    const [isSortOpen, handleSortOpen] = useState<false | number>(false);
    const [isLoading, setLoading] = useState(false);

    const { control, reset, handleSubmit } = useForm<{ value: string; orderItem: IPriceAttr }>();

    const handleSave = handleSubmit(async (data) => {
        const response = await FetchApi.post<Array<IPriceAttrGroup>>(pathsApi.priceAttrSave, {
            value: data.value,
            order: data.orderItem.order || 100,
            group: isNewOpen,
        });

        if (response) {
            setData(response);
            handleNewOpen(false);
            reset();
        }
        setLoading(false);
    });

    const dataMemo = useMemo(() => {
        return data?.map((group) => (
            <Details key={group.id} label={group.value}>
                <GridRow gutter={4}>
                    <GridCol col={12}>
                        <GridRow isFlex gutter={8}>
                            <Button
                                isSmall
                                variation={ButtonVariationEnum.SOFT}
                                onClick={() => handleNewOpen(group.id)}
                            >
                                {i18n.price.newAttr}
                            </Button>
                            <Button
                                isSmall
                                variation={ButtonVariationEnum.SOFT}
                                onClick={() => handleSortOpen(group.id)}
                            >
                                {i18n.general.sort}
                            </Button>
                        </GridRow>
                    </GridCol>
                    {group.items
                        .sort((a, b) =>
                            Number.isInteger(a.order) && Number.isInteger(b.order)
                                ? (a.order as number) - (b.order as number)
                                : new Intl.Collator('ru').compare(a.value, b.value),
                        )
                        .map((item) => (
                            <GridCol col={3} key={item.id}>
                                <Card>{item.value}</Card>
                            </GridCol>
                        ))}
                </GridRow>
            </Details>
        ));
    }, [data]);

    const handleNewClose = () => {
        reset();
        handleNewOpen(false);
    };

    const handleSaveSort = async (payload: Array<{ id: number; order: number }>) => {
        const response = await FetchApi.post<Array<IPriceAttrGroup>>(pathsApi.priceAttrSortSave, {
            items: payload,
        });

        if (response) {
            setData(response);
            return true;
        }
        return false;
    };

    return (
        <Body title={i18n.menu.priceAttr} isLoading={!data}>
            <div>{dataMemo}</div>
            {isNewOpen !== false && (
                <Popup size={PopupSize.s} close={handleNewClose} title={i18n.price.newAttr}>
                    <GridRow gutter={8} column>
                        <Controller
                            control={control}
                            name={'orderItem'}
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Dropdown
                                    isValueObject
                                    emptyFirstline
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    errorMessage={error?.message}
                                    options={data?.find((group) => group.id === isNewOpen)?.items || []}
                                    label={i18n.price.insertAfter}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name={'value'}
                            defaultValue={''}
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    errorMessage={error?.message}
                                    label={i18n.general.name}
                                />
                            )}
                        />
                        <Button onClick={handleSave} isLoading={isLoading}>
                            {i18n.general.save}
                        </Button>
                    </GridRow>
                </Popup>
            )}
            {isSortOpen !== false && (
                <DragAndDropPopup
                    isOpen={!!isSortOpen}
                    handleClose={() => handleSortOpen(false)}
                    items={data
                        ?.find((group) => group.id === isSortOpen)
                        ?.items.map((item) => ({
                            id: item.id,
                            content: item.value,
                        }))}
                    submit={handleSaveSort}
                />
            )}
        </Body>
    );
};

export default PriceAttr;
